import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import TabList from '../components/TabList';
import { inputStyle } from '../components/FormField';
import * as stockApi from '../services/stockAnalysisApi';
import OrderlessOrderBuilder, { orderlessOrderKey, makeOrderLine } from '../components/OrderlessOrderBuilder';
import { HoldView } from './ProductionPlanningPage';
import * as planningApi from '../services/productionPlanningApi';

const FLAG_TABS = [
  { key: '', label: 'All Stock' },
  { key: 'negative', label: 'Negative', tone: 'danger' },
  { key: 'low', label: 'Low Stock', tone: 'warning' },
  { key: 'dead', label: 'Dead Stock', tone: 'text-muted' },
  { key: 'slow', label: 'Slow Moving', tone: 'warning' },
  { key: 'fast', label: 'Fast Moving', tone: 'success' },
];

const DIMENSIONS = [
  { key: 'baseDesign', label: 'Base Design' },
  { key: 'category', label: 'Category' },
  { key: 'size', label: 'Size' },
];

// Duplicate-design merging (same base design name / size) now happens on
// every tab, backend-side (see stockAnalysisService.js). This set is only
// about the single Low/Medium/Fast movement label replacing the multi-flag
// text — that's still specific to these three tabs.
const SYNCHRONIZED_TABS = new Set(['low', 'slow', 'fast']);

// Qty/Aging/Health sort as real numbers, not text — module-level so this
// Set isn't rebuilt (and doesn't need to be a hook dependency) on every render.
const NUMERIC_SORT_KEYS = new Set(['current_qty', 'aging_days', 'health_score']);

/** A labeled "square" container — used for the Classified size cards and
 * the Orderless Stock size cards, each with their own heading above a
 * bordered box holding every card for that group. */
function CardSquare({ label, color, children }) {
  return (
    <div
      style={{
        border: `1px solid ${color || 'var(--color-border)'}`,
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-4)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: color || 'var(--color-text-muted)',
          marginBottom: 'var(--space-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

function SizeCard({ label, value, accent, onClick, isActive }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      style={{
        background: isActive ? 'var(--color-accent-soft)' : accent ? 'var(--color-accent-soft)' : 'var(--color-surface)',
        border: isActive ? '2px solid var(--color-accent)' : accent ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-3) var(--space-4)',
        minWidth: 140,
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-xs)', color: accent ? 'var(--color-accent)' : 'var(--color-text-muted)', fontWeight: accent ? 600 : 400 }}>
        {label}
      </div>
      <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: accent ? 'var(--color-accent)' : 'var(--color-text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

/** The Stock Analysis card drill-down — one row per (design, batch/shade),
 * with its PRE/STD/COM/ECO breakdown, weight per box, and total weight
 * (weight * boxes), ending in a Grand Total row summing every quantity
 * column. Shown when a size card (or Total Box) is clicked. */
function OrderlessStockDetailTable({ sizeName, data, loading, selectedKeys, onToggleRows, title, showLocation, showStatus, hideEco, emptyText }) {
  const BORDER = '1px solid var(--color-border)';
  const thLined = { padding: '8px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderRight: BORDER };
  const tdLined = { padding: '8px 4px', fontSize: 'var(--font-size-sm)', borderRight: BORDER };
  const tdTotal = { padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: BORDER };
  // Distinct design count shown next to the Design heading, same as the
  // Classified detail table.
  const [designQ, setDesignQ] = React.useState('');
  const [boxQ, setBoxQ] = React.useState('');
  const [locQ, setLocQ] = React.useState('');
  React.useEffect(() => { setDesignQ(''); setBoxQ(''); setLocQ(''); }, [sizeName]);
  const dq = designQ.trim().toLowerCase();
  const bq = boxQ.trim().toLowerCase();
  const lq = locQ.trim().toLowerCase();
  const selectable = typeof onToggleRows === 'function';
  const allRows = data?.rows || [];
  const visibleRows = allRows.filter((r) => (!dq || String(r.design_name || '').toLowerCase().includes(dq))
    && (!bq || String(r.box_pack || '').toLowerCase().includes(bq))
    && (!lq || String(r.location_name || '').toLowerCase().includes(lq)));
  const filtering = Boolean(dq || bq || lq);
  const sumOf = (k) => visibleRows.reduce((t, r) => t + (Number(r[k]) || 0), 0);
  const shownTotals = filtering
    ? { pre: sumOf('pre'), std: sumOf('std'), com: sumOf('com'), eco: sumOf('eco'), qty: sumOf('qty'), totalWeight: sumOf('totalWeight') }
    : data?.totals;
  const orderlessDesignCount = new Set(visibleRows.map((r) => r.design_name)).size;
  // Order builder: only rows with stock can be ticked; the header box ticks every shown, orderable row.
  const orderableRows = visibleRows.filter((r) => Number(r.qty) > 0);
  const allShownSelected = orderableRows.length > 0 && orderableRows.every((r) => selectedKeys && selectedKeys.has(orderlessOrderKey(r)));
  const findInput = (value, onChange, placeholder) => (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ display: 'block', width: '100%', minWidth: 80, marginTop: 4, padding: '3px 6px', fontSize: 'var(--font-size-xs)', fontWeight: 400, color: '#1e293b', background: '#fff', border: '1px solid var(--color-border)', borderRadius: 4, boxSizing: 'border-box' }}
    />
  );
  // Columns before the PRE column (checkbox, Design, Batch/Shade, BoxPack, Size, Location).
  const leadCols = (selectable ? 1 : 0) + 3 + (sizeName ? 0 : 1) + (showLocation ? 1 : 0) + (showStatus ? 1 : 0);
  const qtyCols = (hideEco ? 3 : 4) + 3;

  return (
    <Card title={`${title || 'Orderless Stock Detail'}${sizeName ? ` — ${sizeName}` : ' — All Sizes'}`}>
      {loading ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      ) : (
        <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse', border: BORDER }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: BORDER, background: '#e2e8f0' }}>
                {selectable && (
                  <th style={{ ...thLined, width: 28, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      title="Select all shown rows for the order"
                      checked={allShownSelected}
                      disabled={orderableRows.length === 0}
                      onChange={(e) => onToggleRows(orderableRows, e.target.checked)}
                    />
                  </th>
                )}
                <th style={thLined}>{`Design (${orderlessDesignCount})`}{findInput(designQ, setDesignQ, 'Find design')}</th>
                <th style={thLined}>Batch/Shade</th>
                <th style={thLined}>BoxPack{findInput(boxQ, setBoxQ, 'Find BoxPack')}</th>
                {!sizeName && <th style={thLined}>Size</th>}
                {showLocation && <th style={thLined}>Location{findInput(locQ, setLocQ, 'Find location')}</th>}
                {showStatus && <th style={thLined}>Status</th>}
                <th className="data-figure" style={thLined}>PRE</th>
                <th className="data-figure" style={thLined}>STD</th>
                <th className="data-figure" style={thLined}>COM</th>
                {!hideEco && <th className="data-figure" style={thLined}>ECO</th>}
                <th className="data-figure" style={thLined}>Total</th>
                <th className="data-figure" style={thLined}>Weight/Box</th>
                <th className="data-figure" style={{ ...thLined, borderRight: 'none' }}>Total Weight</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, idx) => (
                <tr key={`${r.design_id}-${r.batch_shade}-${r.location_id || ''}-${idx}`} style={{ borderBottom: BORDER }}>
                  {selectable && (
                    <td style={{ ...tdLined, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(selectedKeys && selectedKeys.has(orderlessOrderKey(r)))}
                        disabled={!(Number(r.qty) > 0)}
                        onChange={(e) => onToggleRows([r], e.target.checked)}
                      />
                    </td>
                  )}
                  <td style={tdLined}>{r.design_name}</td>
                  <td style={tdLined}>{r.batch_shade || '—'}</td>
                  <td style={tdLined}>{r.box_pack || '—'}</td>
                  {!sizeName && <td style={tdLined}>{r.size_name}</td>}
                  {showLocation && <td style={tdLined}>{r.location_name || '—'}</td>}
                  {showStatus && <td style={{ ...tdLined, color: r.status === 'HOLD' ? 'var(--color-warning)' : undefined, fontWeight: r.status === 'HOLD' ? 700 : 400 }}>{r.status || '—'}</td>}
                  <td className="data-figure" style={tdLined}>{r.pre}</td>
                  <td className="data-figure" style={tdLined}>{r.std}</td>
                  <td className="data-figure" style={tdLined}>{r.com}</td>
                  {!hideEco && <td className="data-figure" style={tdLined}>{r.eco}</td>}
                  <td className="data-figure" style={{ ...tdLined, fontWeight: 600, color: 'var(--color-accent)' }}>{r.qty}</td>
                  <td className="data-figure" style={tdLined}>{r.weight !== null && r.weight !== undefined ? r.weight : '—'}</td>
                  <td className="data-figure" style={{ ...tdLined, borderRight: 'none' }}>{r.totalWeight !== null && r.totalWeight !== undefined ? r.totalWeight : '—'}</td>
                </tr>
              ))}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={leadCols + qtyCols} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                    {filtering ? 'No match found.' : (emptyText || 'No Orderless Stock detail available.')}
                  </td>
                </tr>
              )}
              {data && visibleRows.length > 0 && (
                <tr style={{ background: '#e2e8f0', borderTop: '2px solid var(--color-border)' }}>
                  <td colSpan={leadCols} style={{ ...tdTotal, textAlign: 'right' }}>
                    Grand Total
                  </td>
                  <td className="data-figure" style={tdTotal}>{shownTotals.pre}</td>
                  <td className="data-figure" style={tdTotal}>{shownTotals.std}</td>
                  <td className="data-figure" style={tdTotal}>{shownTotals.com}</td>
                  {!hideEco && <td className="data-figure" style={tdTotal}>{shownTotals.eco}</td>}
                  <td className="data-figure" style={tdTotal}>{shownTotals.qty}</td>
                  <td style={{ padding: '8px 4px', borderRight: BORDER }} />
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>{Math.round(shownTotals.totalWeight * 10) / 10}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function HealthBar({ score }) {
  const color = score >= 80 ? 'var(--color-success)' : score >= 50 ? 'var(--color-warning)' : 'var(--color-danger)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 60, height: 6, background: 'var(--color-surface-alt)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', background: color }} />
      </div>
      <span className="data-figure" style={{ fontSize: 'var(--font-size-xs)', color }}>{score}</span>
    </div>
  );
}

function SortableHeader({ label, sortKey, activeSort, onSort, lined }) {
  const isActive = activeSort.key === sortKey;
  const arrow = isActive ? (activeSort.direction === 'asc' ? ' \u2191' : ' \u2193') : '';
  return (
    <th
      role="button"
      tabIndex={0}
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSort(sortKey); }}
      style={{
        padding: '8px 4px',
        fontSize: 'var(--font-size-xs)',
        color: isActive ? 'var(--color-accent)' : (lined ? '#1e293b' : 'var(--color-text-muted)'),
        cursor: 'pointer',
        userSelect: 'none',
        fontWeight: (isActive || lined) ? 700 : 400,
      }}
    >
      {label}{arrow}
    </th>
  );
}

function StockTable({ items, showMovementSpeed, sort, onSort, headerLined, designSearch, onDesignSearch }) {
  // Distinct design count for the "(N)" shown next to the Design heading —
  // counts unique design names actually in THIS filtered/tab view, not the
  // whole company's stock, so it matches what the tab is showing right now.
  const designCount = new Set(items.map((r) => r.design_name)).size;

  return (
    <div className="table-scroll">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)', ...(headerLined ? { background: '#e2e8f0' } : {}) }}>
          {onDesignSearch ? (
            <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: '#1e293b', fontWeight: 700 }}>
              {`Design (${designCount})`}
              <input
                type="text"
                value={designSearch || ''}
                onChange={(e) => onDesignSearch(e.target.value)}
                placeholder="Find design"
                style={{ display: 'block', width: '100%', minWidth: 80, marginTop: 4, padding: '3px 6px', fontSize: 'var(--font-size-xs)', fontWeight: 400, color: '#1e293b', background: '#fff', border: '1px solid var(--color-border)', borderRadius: 4, boxSizing: 'border-box' }}
              />
            </th>
          ) : (
            <SortableHeader label={`Design (${designCount})`} sortKey="design_name" activeSort={sort} onSort={onSort} lined={headerLined} />
          )}
          <SortableHeader label="Size" sortKey="size_name" activeSort={sort} onSort={onSort} lined={headerLined} />
          <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: headerLined ? '#1e293b' : 'var(--color-text-muted)', ...(headerLined ? { fontWeight: 700 } : {}) }}>Category</th>
          <SortableHeader label="Qty" sortKey="current_qty" activeSort={sort} onSort={onSort} lined={headerLined} />
          <SortableHeader label="Aging" sortKey="aging_days" activeSort={sort} onSort={onSort} lined={headerLined} />
          <SortableHeader label="Health" sortKey="health_score" activeSort={sort} onSort={onSort} lined={headerLined} />
          <SortableHeader label={showMovementSpeed ? 'Movement' : 'Flags'} sortKey="flagSortValue" activeSort={sort} onSort={onSort} lined={headerLined} />
        </tr>
      </thead>
      <tbody>
        {items.map((row) => (
          <tr key={`${row.design_name}-${row.size_name}-${row.summary_id || row.design_id}`} style={{ borderBottom: '1px solid var(--color-border)' }}>
            <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.design_name}</td>
            <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.size_name || '—'}</td>
            <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.category_name}</td>
            <td
              className="data-figure"
              style={{
                padding: '8px 4px',
                fontSize: 'var(--font-size-sm)',
                color: row.current_qty < 0 ? 'var(--color-danger)' : 'var(--color-text-primary)',
              }}
            >
              {row.current_qty}
            </td>
            <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
              {row.aging_days ?? '—'}d
            </td>
            <td style={{ padding: '8px 4px' }}>
              <HealthBar score={row.health_score} />
            </td>
            <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)' }}>
              {showMovementSpeed ? (
                row.movement_speed
              ) : (
                <>
                  {row.is_negative ? 'Negative ' : ''}
                  {row.is_low_stock ? 'Low ' : ''}
                  {row.is_dead_stock ? 'Dead ' : ''}
                  {row.is_slow_moving ? 'Slow ' : ''}
                  {row.is_fast_moving ? 'Fast ' : ''}
                </>
              )}
            </td>
          </tr>
        ))}
        {items.length === 0 && (
          <tr>
            <td colSpan={7} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
              No stock matches this filter.
            </td>
          </tr>
        )}
      </tbody>
    </table>
    </div>
  );
}

export default function StockAnalysisPage() {
  const [flag, setFlag] = useState('');
  const [items, setItems] = useState([]);
  const [dimension, setDimension] = useState('baseDesign');
  const [breakdown, setBreakdown] = useState([]);
  const [sizeCards, setSizeCards] = useState([]);
  const [orderlessStockCards, setOrderlessStockCards] = useState([]);
  const [reorders, setReorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [designFilter, setDesignFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [designOptions, setDesignOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  // Which card's drill-down detail is open — null (closed), 'all' (Total
  // Box card), or a size id. detailSizeName tracks the clicked card's
  // label so the table header can show it without a lookup.
  const [detailKey, setDetailKey] = useState(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdData, setHoldData] = useState(null);
  const [holdSize, setHoldSize] = useState('');
  const [detailMode, setDetailMode] = useState('total'); // which group's card is open: free / hold / total
  // Orderless Stock order builder: rows ticked in the detail table become order lines.
  const [orderLines, setOrderLines] = useState([]);
  const toggleOrderRows = (rows, checked) => setOrderLines((prev) => {
    const map = new Map(prev.map((l) => [l.key, l]));
    rows.forEach((r) => {
      const key = orderlessOrderKey(r);
      if (checked) {
        const line = makeOrderLine(r);
        if (line && !map.has(key)) map.set(key, line);
      } else {
        map.delete(key);
      }
    });
    return Array.from(map.values());
  });
  const changeOrderQty = (key, value) => setOrderLines((prev) => prev.map((l) => {
    if (l.key !== key) return l;
    if (value === '') return { ...l, qty: '' };
    const n = Math.floor(Number(value));
    if (!Number.isFinite(n)) return l;
    return { ...l, qty: Math.max(0, Math.min(l.maxQty, n)) };
  }));
  const removeOrderLine = (key) => setOrderLines((prev) => prev.filter((l) => l.key !== key));
  const clearOrder = () => setOrderLines([]);
  const [detailSizeName, setDetailSizeName] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Same idea, for clicking a Classified card — shows the regular Stock
  // list (Design/Size/Category/Qty/Aging/Health/Flags) filtered to that
  // size, independent of whichever flag tab (All/Negative/Low/...) is
  // currently active below, since clicking a card is "show me everything
  // for this size" regardless of the tab.
  const [classifiedDetailKey, setClassifiedDetailKey] = useState(null);
  const [classifiedDetailSizeName, setClassifiedDetailSizeName] = useState('');
  const [classifiedDetailData, setClassifiedDetailData] = useState(null);
  const [classifiedDetailLoading, setClassifiedDetailLoading] = useState(false);

  const loadList = (f) => {
    const params = {};
    if (f) params.flag = f;
    if (designFilter) params.design = designFilter;
    if (sizeFilter) params.size = sizeFilter;
    return stockApi.listStock(params).then(setItems);
  };
  const loadBreakdown = (dim) => stockApi.getBreakdown(dim).then(setBreakdown);
  const loadSizeCards = () => stockApi.getBreakdown('size').then(setSizeCards);
  const loadOrderlessStockCards = () => stockApi.getOrderlessStockCards().then(setOrderlessStockCards).catch(() => {});

  // The Orderless Stock square should always appear (and list every known
  // size) the same way Classified does, even before ORDERLESS STOCK.xlsx
  // has ever been uploaded — otherwise there is no way to tell the module
  // exists at all versus simply being broken. Classified's sizeCards is
  // the known-size list (it comes from the regular Stock upload, which
  // runs daily already); any size with no Orderless Stock data yet shows
  // as 0 rather than being left out.
  const orderlessStockCardsDisplay = React.useMemo(() => {
    const bySize = new Map(orderlessStockCards.map((s) => [s.name, s]));
    return sizeCards.map((s) => {
      const o = bySize.get(s.name) || {};
      const totalQty = Number(o.totalQty) || 0;
      const holdQty = Number(o.holdQty) || 0;
      return { id: s.id, name: s.name, totalQty, holdQty, freeQty: totalQty - holdQty };
    });
  }, [sizeCards, orderlessStockCards]);

  // Clicking the same Classified card again closes the drill-down;
  // clicking a different one (or Total Box) switches to it. 'all' fetches
  // every size (no size filter); a specific size id fetches just that one.
  const handleClassifiedCardClick = (key, label) => {
    if (classifiedDetailKey === key) {
      setClassifiedDetailKey(null);
      setClassifiedDetailData(null);
      return;
    }
    setClassifiedDetailKey(key);
    setClassifiedDetailSizeName(key === 'all' ? '' : label);
    setClassifiedDetailLoading(true);
    stockApi.getClassifiedStockDetail(key === 'all' ? undefined : key)
      .then(setClassifiedDetailData)
      .catch(() => setClassifiedDetailData(null))
      .finally(() => setClassifiedDetailLoading(false));
  };

  // Clicking the same card again closes the drill-down; clicking a
  // different card (or Total Box) switches to it. 'all' (Total Box) is
  // passed as undefined to the API, which then returns every size's rows.
  const handleCardClick = (key, label, mode = 'total') => {
    if (mode === 'hold') {
      // HOLD cards open the same full hold page as Production Planning > Calendar
      setHoldSize(key === 'all' ? '' : label);
      setHoldOpen(true);
      planningApi.getProductionHold().then(setHoldData).catch(() => setHoldOpen(false));
      return;
    }
    if (detailKey === key && detailMode === mode) {
      setDetailKey(null);
      setDetailData(null);
      return;
    }
    setDetailKey(key);
    setDetailMode(mode);
    setDetailSizeName(key === 'all' ? '' : label);
    setDetailLoading(true);
    stockApi.getOrderlessStockDetail(key === 'all' ? undefined : key, mode === 'total' ? undefined : mode)
      .then(setDetailData)
      .catch(() => setDetailData(null))
      .finally(() => setDetailLoading(false));
  };

  // Design/Size dropdown option lists — loaded once; they reflect every
  // design/size currently in stock, not just what the active tab/filter
  // shows, so choosing one can broaden the view rather than only narrow it.
  useEffect(() => {
    stockApi.getDesignOptions().then(setDesignOptions).catch(() => {});
    stockApi.getSizeOptions().then(setSizeOptions).catch(() => {});
    loadSizeCards().catch(() => {});
    loadOrderlessStockCards();
  }, []);

  useEffect(() => {
    setLoading(true);
    const tasks = [loadList(flag)];
    if (flag === 'low') tasks.push(stockApi.getReorderSuggestions().then(setReorders));
    Promise.all(tasks)
      .catch((err) => setError(err.message || 'Could not load stock analysis.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag, designFilter, sizeFilter]);

  useEffect(() => {
    loadBreakdown(dimension).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimension]);

  const handleSort = (key) => {
    setSort((prev) => (prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }));
  };

  // Numeric fields sort as numbers (so 9 comes before 10, and negative qty
  // sorts correctly below positive) — Design/Size/Movement-text stay on the
  // locale-aware text sort. Flags (when not showing the Low/Medium/Fast
  // movement label) don't have one natural field to sort by, so each row's
  // flags become a single severity number — more flags true = higher —
  // giving the low-to-high / high-to-low toggle a meaningful order instead
  // of an arbitrary one.
  const itemsWithFlagSort = React.useMemo(() => items.map((r) => ({
    ...r,
    flagSortValue: SYNCHRONIZED_TABS.has(flag)
      ? r.movement_speed
      : Number(!!r.is_negative) + Number(!!r.is_low_stock) + Number(!!r.is_dead_stock)
        + Number(!!r.is_slow_moving) + Number(!!r.is_fast_moving),
  })), [items, flag]);

  const sortedItems = React.useMemo(() => {
    if (!sort.key) return itemsWithFlagSort;
    const copy = [...itemsWithFlagSort];
    const isNumeric = NUMERIC_SORT_KEYS.has(sort.key) || (sort.key === 'flagSortValue' && !SYNCHRONIZED_TABS.has(flag));
    copy.sort((a, b) => {
      if (isNumeric) {
        const av = Number(a[sort.key]) || 0;
        const bv = Number(b[sort.key]) || 0;
        return sort.direction === 'asc' ? av - bv : bv - av;
      }
      const av = (a[sort.key] || '').toString();
      const bv = (b[sort.key] || '').toString();
      const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      return sort.direction === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [itemsWithFlagSort, sort, flag]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await stockApi.refreshStockAnalysis();
      await loadList(flag);
      await loadBreakdown(dimension);
      await loadSizeCards();
      loadOrderlessStockCards();
      stockApi.getDesignOptions().then(setDesignOptions).catch(() => {});
      stockApi.getSizeOptions().then(setSizeOptions).catch(() => {});
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Stock Analysis</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Current stock, movement classification, aging, and reorder suggestions.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh analysis'}
        </Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {sizeCards.length > 0 && (
        <CardSquare label="Classified">
          <SizeCard
            label="Total Box"
            value={sizeCards.reduce((sum, s) => sum + (Number(s.totalQty) || 0), 0)}
            accent
            isActive={classifiedDetailKey === 'all'}
            onClick={() => handleClassifiedCardClick('all', '')}
          />
          {sizeCards.map((s) => (
            <SizeCard
              key={s.id}
              label={s.name}
              value={s.totalQty}
              isActive={classifiedDetailKey === s.id}
              onClick={() => handleClassifiedCardClick(s.id, s.name)}
            />
          ))}
        </CardSquare>
      )}

      {classifiedDetailKey !== null && (
        <OrderlessStockDetailTable
          title="Classified Detail"
          showLocation
          hideEco
          emptyText="No Classified stock detail available."
          sizeName={classifiedDetailKey === 'all' ? '' : classifiedDetailSizeName}
          data={classifiedDetailData}
          loading={classifiedDetailLoading}
        />
      )}

      {orderlessStockCardsDisplay.length > 0 && (() => {
        // Free orderless + On HOLD = Total orderless. Every card opens its own detail table.
        const list = orderlessStockCardsDisplay;
        const hasHold = list.some((s) => s.holdQty > 0);
        const groups = hasHold
          ? [
            { mode: 'free', title: 'Free orderless', color: 'var(--color-accent)', field: 'freeQty' },
            { mode: 'hold', title: 'On HOLD', color: 'var(--color-warning)', field: 'holdQty' },
            { mode: 'total', title: 'Total orderless', color: 'var(--color-text-primary)', field: 'totalQty' },
          ]
          : [{ mode: 'total', title: '', color: 'var(--color-warning)', field: 'totalQty' }];
        const isOn = (key, mode) => detailKey === key && detailMode === mode;
        return (
          <CardSquare label="Orderless Stock" color="var(--color-warning)">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-3)', width: '100%' }}>
              {groups.map((g, gi) => {
                const sizes = hasHold ? list.filter((s) => s[g.field] > 0) : list;
                return (
                  <React.Fragment key={g.mode}>
                    {gi > 0 && (
                      <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: 'var(--color-text-muted)', padding: '0 4px' }}>
                        {gi === groups.length - 1 ? '=' : '+'}
                      </div>
                    )}
                    <div
                      style={{
                        border: hasHold ? `1px dashed ${g.color}` : 'none',
                        borderRadius: 'var(--radius-md)',
                        padding: hasHold ? 'var(--space-2)' : 0,
                      }}
                    >
                      {hasHold && (
                        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: g.color, marginBottom: 'var(--space-2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {g.title}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <SizeCard
                          label="Total Box"
                          value={sizes.reduce((t, s) => t + (Number(s[g.field]) || 0), 0)}
                          accent
                          isActive={isOn('all', g.mode)}
                          onClick={() => handleCardClick('all', '', g.mode)}
                        />
                        {sizes.map((s) => (
                          <SizeCard
                            key={s.id}
                            label={s.name}
                            value={s[g.field]}
                            isActive={isOn(s.id, g.mode)}
                            onClick={() => handleCardClick(s.id, s.name, g.mode)}
                          />
                        ))}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </CardSquare>
        );
      })()}

      {holdOpen && holdData && <HoldView data={holdData} initialSize={holdSize} onClose={() => setHoldOpen(false)} />}

      {detailKey !== null && detailMode !== 'hold' && (
        <OrderlessStockDetailTable
          title={detailMode === 'hold' ? 'On HOLD Detail' : detailMode === 'free' ? 'Free Orderless Detail' : 'Total Orderless Detail'}
          showStatus
          sizeName={detailKey === 'all' ? '' : detailSizeName}
          data={detailData}
          loading={detailLoading}
          selectedKeys={new Set(orderLines.map((l) => l.key))}
          onToggleRows={detailMode === 'hold' ? undefined : toggleOrderRows}
        />
      )}

      <OrderlessOrderBuilder lines={orderLines} onQtyChange={changeOrderQty} onRemove={removeOrderLine} onClear={clearOrder} />

      <TabList tabs={FLAG_TABS} activeKey={flag} onChange={setFlag} ariaLabel="Stock filter" />

      <div style={{ display: 'flex', gap: 'var(--space-3)', margin: 'var(--space-3) 0', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          Design
          <select value={designFilter} onChange={(e) => setDesignFilter(e.target.value)} style={{ ...inputStyle, width: 220 }}>
            <option value="">All designs</option>
            {designOptions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          Size
          <select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)} style={{ ...inputStyle, width: 160 }}>
            <option value="">All sizes</option>
            {sizeOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        {(designFilter || sizeFilter) && (
          <Button variant="text" onClick={() => { setDesignFilter(''); setSizeFilter(''); }} style={{ alignSelf: 'flex-end' }}>
            Clear filters
          </Button>
        )}
      </div>

      <Card title={FLAG_TABS.find((t) => t.key === flag)?.label || 'Stock'}>
        {loading ? <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p> : <StockTable items={sortedItems} showMovementSpeed={SYNCHRONIZED_TABS.has(flag)} sort={sort} onSort={handleSort} />}
      </Card>

      {flag === 'low' && reorders.length > 0 && (
        <Card title="Reorder Suggestions">
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                {['Design', 'Current Qty', 'Safety Stock', 'Suggested Reorder Qty'].map((h) => (
                  <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reorders.map((r) => (
                <tr key={r.summary_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{r.design_name}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{r.current_qty}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{r.safety_stock_qty}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-accent)' }}>
                    {r.reorder_suggested_qty}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {flag === 'low' && (
        <Card
          title="Breakdown"
          actions={
            <select value={dimension} onChange={(e) => setDimension(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              {DIMENSIONS.map((d) => (
                <option key={d.key} value={d.key}>{d.label}-wise</option>
              ))}
            </select>
          }
        >
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                {['Name', 'Total Qty', 'Lines', 'Negative', 'Low', 'Dead'].map((h) => (
                  <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {breakdown.map((b) => (
                <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.name}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.totalQty}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.lineCount}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.negativeCount}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.lowStockCount}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.deadStockCount}</td>
                </tr>
              ))}
              {breakdown.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>No data.</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </Card>
      )}
    </div>
  );
}
